import { format, formatDistanceToNowStrict, parseISO } from "date-fns";
import type { Money } from "@/lib/api/types";

/* --- money --------------------------------------------------------------- */

const gbp0 = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});
const gbp2 = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** The API pre-formats money. Only fall back when it is absent. */
export function money(value: Money | null | undefined, fallback = "—"): string {
  if (!value) return fallback;
  if (value.formatted) return value.formatted;
  return Math.abs(value.pounds) >= 1000 ? gbp0.format(value.pounds) : gbp2.format(value.pounds);
}

export function pounds(value: number | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return Math.abs(value) >= 1000 ? gbp0.format(value) : gbp2.format(value);
}

/**
 * Some endpoints return raw pence integers rather than the Money object
 * (`GET /v1/reference/plans` gives `monthlyPence`), so this is the one place
 * that divides by 100.
 */
export function fromPence(pence: number | null | undefined, fallback = "—"): string {
  if (pence === null || pence === undefined || Number.isNaN(pence)) return fallback;
  return pounds(pence / 100);
}

/** £218,400 → "£218k"; for axis ticks and tight stat tiles. */
export function compactMoney(poundsValue: number | null | undefined): string {
  if (poundsValue === null || poundsValue === undefined || Number.isNaN(poundsValue)) return "—";
  const abs = Math.abs(poundsValue);
  const sign = poundsValue < 0 ? "-" : "";
  if (abs >= 1_000_000_000) return `${sign}£${trim(abs / 1_000_000_000)}bn`;
  if (abs >= 1_000_000) return `${sign}£${trim(abs / 1_000_000)}m`;
  if (abs >= 1_000) return `${sign}£${trim(abs / 1_000)}k`;
  return `${sign}£${Math.round(abs)}`;
}

function trim(n: number) {
  return (Math.round(n * 10) / 10).toString();
}

export function moneyPounds(value: Money | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  return value.pounds ?? null;
}

/* --- numbers ------------------------------------------------------------- */

const numberFmt = new Intl.NumberFormat("en-GB");

export function number(value: number | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return numberFmt.format(value);
}

/** The API returns percentages as plain decimals: 65.17 means 65.17%. */
export function percent(value: number | null | undefined, digits = 1, fallback = "—"): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  const rounded = Number(value.toFixed(digits));
  return `${rounded}%`;
}

export function signedPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Number(value.toFixed(digits))}%`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${i === 0 ? n : n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds && seconds !== 0) return "";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

export function clockTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/* --- periods (YYYY-MM) --------------------------------------------------- */

export function currentPeriod(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split("-").map(Number);
  const date = new Date(Date.UTC(y, (m ?? 1) - 1 + months, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function isValidPeriod(period: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(period);
}

export function isFuturePeriod(period: string): boolean {
  return isValidPeriod(period) && period > currentPeriod();
}

/** "2026-07" → "Jul 2026" (or "Jul" with `short`). */
export function formatPeriod(period: string | null | undefined, style: "long" | "short" = "long"): string {
  if (!period || !isValidPeriod(period)) return period ?? "—";
  const [y, m] = period.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, 1));
  const monthName = date.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
  return style === "short" ? monthName : `${monthName} ${y}`;
}

/** "2027-02" → "2027-02-28"; for endpoints that want a date, not a period. */
export function endOfMonthISO(period: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${period}-${String(lastDay).padStart(2, "0")}`;
}

export function periodRange(endPeriod: string, months: number): string[] {
  return Array.from({ length: months }, (_, i) => shiftPeriod(endPeriod, -(months - 1 - i)));
}

/* --- dates --------------------------------------------------------------- */

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === "string" ? parseISO(value) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value: string | Date | null | undefined, pattern = "d MMM yyyy"): string {
  const d = toDate(value);
  return d ? format(d, pattern) : "—";
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  return d ? format(d, "d MMM yyyy, HH:mm") : "—";
}

export function timeAgo(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  return `${formatDistanceToNowStrict(d)} ago`;
}

/* --- text ---------------------------------------------------------------- */

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

export function titleCase(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * `missingFields[]` comes back as database column names — `revenue_pence`,
 * `cost_of_sales_pence`, `customers_total`. Turn those into the words a person
 * would use, rather than showing them the schema.
 */
export function humaniseField(key: string): string {
  const overrides: Record<string, string> = {
    cost_of_sales: "cost of sales",
    cash_at_bank: "cash at bank",
    pipeline_value: "pipeline value",
    customers_total: "total customers",
    customers_new: "new customers",
    customers_churned: "churned customers",
    gross_profit: "gross profit",
    net_profit: "net profit",
    headcount: "headcount",
    overheads: "overheads",
    revenue: "revenue",
    debt: "debt",
    notes: "notes",
  };

  // camelCase → snake_case first, then strip the storage-unit suffixes so both
  // naming styles land on the same key.
  const normalised = key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/_(pence|pounds|cents|amount|count)$/, "")
    .replace(/[\s-]+/g, "_");

  const words = overrides[normalised] ?? normalised.replace(/_+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
